import SongStore from '../stores/SongStore';
import BeatGridPositionUpdateAction from '../actions/BeatGridPositionUpdateAction';
import VUMeterUpdateAction from '../actions/VUMeterUpdateAction';

class AudioLayer {

	constructor(dispatcher) {
		this.dispatcher = dispatcher;
		this.context = false;

		this.current16thNote = 0;
		this.nextNoteTime = 0;
		this.scheduleAheadTime = 0.2;

		this.tempo = 128;

		this.playing = false;

		this.fftsize = 32;

		this.analyserRunning = false;

	}

	start() {

		var store = this.dispatcher.getStore(SongStore);

		store.on('PLAY_SONG', () => {
            this.startContext();
			setImmediate(() => {
				this.play();
			});
		});

		store.on('STOP_SONG', () => {
			this.stop();
		});

		store.on("AUDIO_GRAPH_INSTRUMENT_SWAP", (payload) => {
			this.handleInstrumentSwap(payload);
		});

		store.on("AUDIO_GRAPH_EFFECT_ADDED", (payload) => {
			this.handleAddedEffect(payload);
		});

		store.on("AUDIO_GRAPH_EFFECT_MOVED", (payload) => {
			setImmediate(() => {
				this.handleEffectMove(payload);
			})
		});

		store.on("TEMPO_CHANGE", (payload) => {
			this.tempo = payload.tempo;
		});

		store.on("AUDIO_GRAPH_REBUILD", () => {
			this.rebuildAudioGraph();
		});

		store.on("AUDIO_GRAPH_EFFECT_REMOVED", (payload) => {
			this.handleRemoveEffect(payload);
		});

		this.song = store.song;
		this.tempo  = this.song.tempo;
	}

    startContext() {
        window.AudioContext = window.AudioContext||window.webkitAudioContext;
        this.context = new AudioContext();
        this.buildFullAudioGraph();
    }

	buildFullAudioGraph() {
		if(!(this.context instanceof AudioContext)) return;

		this.graph = {channels: {}};

		var masterGainNode = this.context.createGain();
		var masterAnalyser = this.context.createAnalyser();
		var masterPanNode = this.context.createStereoPanner();
		var effect;

		masterGainNode.gain.value = 1;

		for(var i = this.song.channels.length - 1; i >= 0; i--) {
			this.graph.channels[i] = {effects: [], gain: this.context.createGain()};
			var previousNode = this.song.channels[i].instrument.createNodes(this.context);
			this.graph.channels[i].instrument = previousNode;
			for(var j = 0, k = this.song.channels[i].effects.length; j < k; j++) {
				effect = this.song.channels[i].effects[j];
				var node = effect.createNodes(this.context);
				this.graph.channels[i].effects.push(node);
				previousNode.connect(node.input);
				previousNode = node;
			}
			previousNode.connect(this.graph.channels[i].gain);


			var analyser = this.context.createAnalyser();
			var panNode = this.context.createStereoPanner();

			this.graph.channels[i].analyser = analyser;
			this.graph.channels[i].panner = panNode;

			analyser.fftsize = this.fftsize;

			this.graph.channels[i].buffer = new Uint8Array(this.fftsize*2);

			this.song.channels[i].gain.setParam(this.graph.channels[i].gain.gain);
			this.song.channels[i].pan.setParam(this.graph.channels[i].panner.pan);

			this.graph.channels[i].gain.connect(this.graph.channels[i].panner);



			this.graph.channels[i].panner.connect(analyser);
			analyser.connect(masterGainNode);

		}

		this.song.masterChannel.gain.setParam(masterGainNode.gain);
		this.song.masterChannel.pan.setParam(masterPanNode.pan);

		masterGainNode.connect(masterPanNode);
		masterPanNode.connect(masterAnalyser);
		masterAnalyser.connect(this.context.destination);


		this.graph.master = {
			gain: masterGainNode,
			panner: masterPanNode,
			analyser: masterAnalyser,
			buffer: new Uint8Array(this.fftsize*2)
		};

		this.startAnalyserEvents();
	}

	rebuildAudioGraph() {

        if(this.context instanceof AudioContext) {
            this.analyserRunning = false;
            if(this.graph.master) {
                this.graph.master.analyser.disconnect(this.context.destination);
            }
        }

		var store = this.dispatcher.getStore(SongStore);

		this.song = store.song;
		this.tempo  = this.song.tempo;

		this.buildFullAudioGraph();
	}

	getAnalyserData() {

		var buffer, analyser, level, i, j, len, x;

		var vuData = [];

		for(i = 0, len = this.song.channels.length; i < len; i++) {
			buffer = this.graph.channels[i].buffer;
			analyser = this.graph.channels[i].analyser;
			analyser.getByteFrequencyData(buffer);

			level = 0;

			for(j = 0; j < this.fftsize*2; j++) {
				x = buffer[j];
				level += x * x;
			}

			vuData.push(Math.sqrt(level / (this.fftsize*2)));

		}

		buffer = this.graph.master.buffer;
		analyser = this.graph.master.analyser;
		analyser.getByteFrequencyData(buffer);

		level = 0;

		for(j = 0; j < this.fftsize*2; j++) {
			x = buffer[j];
			level += x * x;
		}

		vuData.push(Math.sqrt(level / (this.fftsize*2)));

		this.dispatcher.executeAction(VUMeterUpdateAction, vuData);

		if(this.analyserRunning){
			requestAnimationFrame(this.getAnalyserData.bind(this));
		}
	}

	startAnalyserEvents() {
		if(this.analyserRunning) return;
		this.analyserRunning = true;
		requestAnimationFrame(this.getAnalyserData.bind(this));
	}

	handleEffectMove(data) {

        if(!(this.context instanceof AudioContext)) return;

		var gc = this.graph.channels[data.channel];
		var fx = gc.effects;

		var to = data.to;
		var from = data.from;
		var last = fx.length-1;
		var diff = data.to-data.from;

		if(from === last) {
			fx[from - 1].disconnect(fx[from].input);
			fx[from].disconnect(gc.gain);

			if (to === 0) {
				gc.instrument.disconnect(fx[to].input);
			} else {
				if (diff === -1) {
					fx[to-1].disconnect(fx[to].input);
				} else {
					fx[to-1].disconnect(fx[to].input);
					fx[to].disconnect(fx[to+1].input);
				}
			}

		} else if(from === 0) {
			gc.instrument.disconnect(fx[from].input);
			fx[from].disconnect(fx[from+1].input);
			if(to === last) {
				fx[to].disconnect(gc.gain);
			} else {
				if (diff === 1) {
					fx[to].disconnect(fx[to+1].input);
				} else {
					fx[to-1].disconnect(fx[to].input);
					fx[to].disconnect(fx[to+1].input);
				}
			}
		} else {
			fx[from-1].disconnect(fx[from].input);
			fx[from].disconnect(fx[from+1].input);

			if(to === 0) {
				gc.instrument.disconnect(fx[to].input);
			} else if(to === last) {
				fx[to].disconnect(gc.gain);
			} else if (diff === -1) {
				fx[to-1].disconnect(fx[to].input);
			} else if (diff === 1) {
				fx[to].disconnect(fx[to + 1].input);
			} else {
				fx[to-1].disconnect(fx[to].input);
				fx[to].disconnect(fx[to+1].input);
			}
		}

		var temp = fx[from];
		fx[from] = fx[to];
		fx[to] = temp;

		if(from === last) {
			fx[from - 1].connect(fx[from].input);
			fx[from].connect(gc.gain);

			if (to === 0) {
				gc.instrument.connect(fx[to].input);
			} else {
				if (diff === -1) {
					fx[to-1].connect(fx[to].input);
				} else {
					fx[to-1].connect(fx[to].input);
					fx[to].connect(fx[to+1].input);
				}
			}

		} else if(from === 0) {
			gc.instrument.connect(fx[from].input);
			fx[from].connect(fx[from+1].input);
			if(to === last) {
				fx[to].connect(gc.gain);
			} else {
				if (diff === 1) {
					fx[to].connect(fx[to+1].input);
				} else {
					fx[to-1].connect(fx[to].input);
					fx[to].connect(fx[to+1].input);
				}
			}
		} else {
			fx[from-1].connect(fx[from].input);
			fx[from].connect(fx[from+1].input);
			if(to === 0) {
				gc.instrument.connect(fx[to].input);
			} else if(to === last) {
				fx[to].connect(gc.gain);
			} else if (diff === -1) {
				fx[to-1].connect(fx[to].input);
			} else if (diff === 1) {
				fx[to].connect(fx[to + 1].input);
			} else {
				fx[to-1].connect(fx[to].input);
				fx[to].connect(fx[to+1].input);
			}
		}

	}

	handleRemoveEffect(data) {

        if(!(this.context instanceof AudioContext)) return;

		var gc = this.graph.channels[data.channel];
		var fx = gc.effects;
		var lastIndex = fx.length-1;

		var rm = data.effect;

		if(lastIndex == 0) {
			gc.instrument.disconnect(fx[rm].input);
			fx[rm].disconnect(gc.gain);
			gc.instrument.connect(gc.gain);
		} else if(lastIndex == rm) {
			fx[rm-1].disconnect(fx[rm].input);
			fx[rm].disconnect(gc.gain);
			fx[rm-1].connect(gc.gain);
		} else if(0 == rm) {
			gc.instrument.disconnect(fx[rm].input);
			fx[rm].disconnect(fx[rm+1].input);
			gc.instrument.connect(fx[rm+1].input);
		} else {
			fx[rm-1].disconnect(fx[rm].input);
			fx[rm].disconnect(fx[rm+1].input);
			fx[rm-1].connect(fx[rm+1].input);
		}

		fx.splice(rm, 1);

	}

	handleAddedEffect(data) {

        if(!(this.context instanceof AudioContext)) return;

		var channel = this.song.channels[data.channel];
		var graphChannel = this.graph.channels[data.channel];

		var lastIndex = channel.effects.length-1;
		var effect = channel.effects[lastIndex];

		var node = effect.createNodes(this.context);
		graphChannel.effects[lastIndex] = node;

		if(lastIndex-1 !== -1) {
			var secondToLastNode = graphChannel.effects[lastIndex-1];
			secondToLastNode.disconnect(graphChannel.gain);
			secondToLastNode.connect(node.input)
		} else {
			graphChannel.instrument.disconnect(graphChannel.gain);
			graphChannel.instrument.connect(node.input);
		}
		node.connect(graphChannel.gain);
	}

	handleInstrumentSwap(data) {

        if(!(this.context instanceof AudioContext)) return;

		var graphChannel = this.graph.channels[data.channel];
		var newInstrument = data.next.createNodes(this.context);
		var oldInstrument = graphChannel.instrument;
		var effects = graphChannel.effects;
		if(effects.length > 0) {
			oldInstrument.disconnect(effects[0].input);
			newInstrument.connect(effects[0].input);
		} else {
			oldInstrument.disconnect(graphChannel.gain);
			newInstrument.connect(graphChannel.gain);
		}
		graphChannel.instrument = newInstrument;
	}

	play() {
		if(this.playing) return;
		this.nextNoteTime = this.context.currentTime;
		this.playing = true;
		this.queueNotes();
	}

	stop() {
		if(this.playing) {
			clearTimeout(this.timeoutId);
			this.playing = false;
			this.current16thNote = 0;
		}
	}

	queueNotes() {

		if(!this.playing) return;

		while (this.nextNoteTime < this.context.currentTime + this.scheduleAheadTime ) {
			this.scheduleNote( this.current16thNote, this.nextNoteTime, this.context.currentTime );
			this.nextNote();
		}

		this.timeoutId = setTimeout(this.queueNotes.bind(this), 25);
	}

	nextNote() {
		// Advance current note and time by a 16th note...
		var secondsPerBeat = 60.0 / this.tempo;    // Notice this picks up the CURRENT
											  // tempo value to calculate beat length.
		this.nextNoteTime += 0.25 * secondsPerBeat;    // Add beat length to last beat time

		this.current16thNote++;    // Advance the beat number, wrap to zero
		if (this.current16thNote == 16) {
			this.current16thNote = 0;
		}
	}

	scheduleNote( beatNumber, time, currentTime ) {

		var notes, s, c;

		for(let i = this.song.channels.length - 1; i >= 0; i--) {
			c = this.song.channels[i];
			if(s = c.getNextSequence(beatNumber)) {

				if(s.selected) {

					setTimeout((beat, channel, number) => {
						this.dispatcher.executeAction(BeatGridPositionUpdateAction, {
							beat: beat,
							channel: channel,
							number: number
						});
					}, time * 1000 - currentTime * 1000, s.currentPlayingBeat, i, s.number);

				}

				c.instrument.scheduleNote(s.getNextNotes(), time, this.song.tonic, this.song.key, this.tempo);
			}
		}
	}

}

export default AudioLayer;
